import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { duration as durationTokens } from "@/lib/motion";

// ─── Types ───────────────────────────────────────────────────────────────────

interface BookSpinnerProps {
  className?: string;
  /** Size variant: sm (24px inline), md (48px card loading), lg (96px full-page). */
  size?: "sm" | "md" | "lg";
  /** Whether to show the "Loading…" text below the spinner. */
  showText?: boolean;
  /** Whether to render centered in a fixed full-screen overlay. */
  fullScreen?: boolean;
}

// ─── Size Configuration ──────────────────────────────────────────────────────

const sizeConfig = {
  sm: {
    width: 24,
    height: 30,
    pageLineCount: 2,
    pageLineGap: 4,
    pageLineOffsetY: 6,
    spineWidth: 1,
    borderWidth: 1.5,
    borderRadius: 2,
    textClass: "text-[10px]",
    gapClass: "gap-1.5",
    pagePadding: 2,
  },
  md: {
    width: 48,
    height: 62,
    pageLineCount: 4,
    pageLineGap: 5,
    pageLineOffsetY: 10,
    spineWidth: 1.5,
    borderWidth: 2,
    borderRadius: 3,
    textClass: "text-xs",
    gapClass: "gap-3",
    pagePadding: 4,
  },
  lg: {
    width: 96,
    height: 120,
    pageLineCount: 6,
    pageLineGap: 8,
    pageLineOffsetY: 16,
    spineWidth: 2,
    borderWidth: 2.5,
    borderRadius: 4,
    textClass: "text-sm",
    gapClass: "gap-4",
    pagePadding: 8,
  },
} as const;

// ─── Animation Constants ─────────────────────────────────────────────────────

const LOOP_DURATION = durationTokens.spinnerLoop; // 1.5s

// Page flip keyframe timings (normalised 0→1)
const PAGE_FLIP_TIMES = [0, 0.35, 0.5, 1] as const;

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Custom animated book-opening loading spinner.
 *
 * The animation shows a book with its right-hand page fanning open and
 * closing in a smooth loop. Decorative "text lines" on the left page
 * pulse gently to suggest content.
 *
 * Three size variants:
 * - **sm** (24 px) — inline usage, e.g. next to a label or inside a button
 * - **md** (48 px) — card-level loading, e.g. inside a card skeleton
 * - **lg** (96 px) — full-page or section loading
 *
 * Colours automatically adapt to the current theme (light / dark) via
 * Tailwind's `hsl(var(--primary))` tokens.
 *
 * Accessibility:
 * - `role="status"` so screen readers announce it
 * - `aria-label` with translated "Loading…" text
 * - Respects `prefers-reduced-motion`: when enabled the page flip is
 *   replaced by a gentle static pulse.
 *
 * @example
 * ```tsx
 * <BookSpinner />                          // md (default)
 * <BookSpinner size="sm" showText={false} /> // inline
 * <BookSpinner size="lg" fullScreen />      // full-page overlay
 * ```
 */
export default function BookSpinner({
  className,
  size = "md",
  showText = true,
  fullScreen = false,
}: BookSpinnerProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const config = sizeConfig[size];

  const loadingText = t("common.loading");

  // ── Reduced-motion fallback: gentle pulse only ───────────────────────────

  const pageFlipAnimation = prefersReducedMotion
    ? { opacity: [0.4, 0.7, 0.4] }
    : { rotateY: [0, -180, -180, 0] };

  const pageFlipTransition = prefersReducedMotion
    ? {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut" as const,
      }
    : {
        duration: LOOP_DURATION + 0.3, // slightly longer for smooth feel
        repeat: Infinity,
        ease: [0.42, 0, 0.58, 1] as [number, number, number, number],
        times: [...PAGE_FLIP_TIMES],
      };

  // ── Build the spinner element ────────────────────────────────────────────

  const spinner = (
    <div
      className={cn(
        "flex flex-col items-center justify-center",
        config.gapClass,
        className,
      )}
      role="status"
      aria-label={loadingText}
    >
      {/* ── Book container ──────────────────────────────────────────── */}
      <div
        className="relative"
        style={{
          width: config.width,
          height: config.height,
          perspective: config.width * 4,
        }}
      >
        {/* Book border / cover */}
        <div
          className="absolute inset-0 rounded-sm border-primary/30 bg-primary/5"
          style={{
            borderWidth: config.borderWidth,
            borderRadius: config.borderRadius,
          }}
        />

        {/* Left page (static) */}
        <div
          className="absolute bg-primary/10"
          style={{
            top: config.pagePadding,
            bottom: config.pagePadding,
            left: config.pagePadding,
            right: `calc(50% + ${config.spineWidth / 2}px)`,
            borderTopLeftRadius: config.borderRadius - 1,
            borderBottomLeftRadius: config.borderRadius - 1,
            transformOrigin: "right center",
          }}
        >
          {/* Decorative text lines on the left page */}
          {Array.from({ length: config.pageLineCount }).map((_, i) => (
            <motion.div
              key={`line-${i}`}
              className="bg-primary/15"
              style={{
                position: "absolute",
                left: config.pagePadding,
                right: `${10 + (i % 3) * 8}%`,
                top: config.pageLineOffsetY + i * config.pageLineGap,
                height: Math.max(1, config.borderWidth * 0.5),
                borderRadius: 1,
              }}
              animate={
                prefersReducedMotion ? undefined : { opacity: [0.3, 0.7, 0.3] }
              }
              transition={
                prefersReducedMotion
                  ? undefined
                  : {
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: i * 0.15,
                    }
              }
            />
          ))}
        </div>

        {/* Right page (flipping) */}
        <motion.div
          className="absolute bg-primary/20"
          style={{
            top: config.pagePadding,
            bottom: config.pagePadding,
            left: `calc(50% + ${config.spineWidth / 2}px)`,
            right: config.pagePadding,
            borderTopRightRadius: config.borderRadius - 1,
            borderBottomRightRadius: config.borderRadius - 1,
            transformOrigin: "left center",
            transformStyle: "preserve-3d",
            backfaceVisibility: "hidden",
          }}
          animate={pageFlipAnimation}
          transition={pageFlipTransition}
        />

        {/* Second right page (visible during flip, behind the first) */}
        <div
          className="absolute bg-primary/8"
          style={{
            top: config.pagePadding + 1,
            bottom: config.pagePadding + 1,
            left: `calc(50% + ${config.spineWidth / 2}px + 1px)`,
            right: config.pagePadding + 1,
            borderTopRightRadius: config.borderRadius - 1,
            borderBottomRightRadius: config.borderRadius - 1,
          }}
        />

        {/* Spine shadow */}
        <motion.div
          className="absolute bg-gradient-to-r from-primary/15 to-transparent"
          style={{
            top: config.pagePadding,
            bottom: config.pagePadding,
            left: `calc(50% - ${config.spineWidth / 2}px)`,
            width: config.spineWidth + 2,
          }}
          animate={
            prefersReducedMotion ? undefined : { opacity: [0.5, 0.85, 0.5] }
          }
          transition={
            prefersReducedMotion
              ? undefined
              : {
                  duration: LOOP_DURATION + 0.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }
          }
        />

        {/* Spine centre line */}
        <div
          className="absolute bg-primary/20"
          style={{
            top: 0,
            bottom: 0,
            left: `calc(50% - ${config.spineWidth / 4}px)`,
            width: Math.max(0.5, config.spineWidth / 2),
          }}
        />
      </div>

      {/* ── Loading text ────────────────────────────────────────────── */}
      {showText && (
        <motion.p
          className={cn(
            "font-medium text-muted-foreground select-none",
            config.textClass,
          )}
          animate={
            prefersReducedMotion ? undefined : { opacity: [0.5, 1, 0.5] }
          }
          transition={
            prefersReducedMotion
              ? undefined
              : {
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }
          }
        >
          {loadingText}
        </motion.p>
      )}

      {/* Screen-reader-only live text */}
      <span className="sr-only">{loadingText}</span>
    </div>
  );

  // ── Full-screen overlay wrapper ──────────────────────────────────────────

  if (fullScreen) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
        >
          {spinner}
        </motion.div>
      </AnimatePresence>
    );
  }

  return spinner;
}
